/*
 * Copyright © Scale Microgrid Solutions Operating, LLC [2023].
 * All rights reserved.
 *
 * SPDX-FileCopyrightText: ©2023 Scale Microgrid Solutions Operating, LLC <legal@scalemicrogrids.com>
 */

import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  Button,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import PropTypes from "prop-types";
import Util, { toISOStringWithTimezone } from "../../../lib/Util";
import { CSVLink } from "react-csv";
import { getSiteTimeSeries, getTimeSeriesMetrics } from "../../../lib/Queries";
import { findSeries } from "../Logic/formatUtils";
import { LoadingButton } from "@mui/lab";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const DownloadDialog = ({
  start,
  stop,
  defaultInterval,
  open,
  onClose,
  siteUuid,
  metricsUuids,
  site,
  siteName,
}) => {
  const [interval, setInterval] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [requestStart, setRequestStart] = useState(start);
  const [requestStop, setRequestStop] = useState(stop);
  const [includeMinMax, setIncludeMinMax] = useState(false);
  const [includeLastValue, setIncludeLastValue] = useState(false);
  const [includeMissingData, setIncludeMissingData] = useState(false);

  const timezone = site?.timezone;

  useEffect(() => {
    setRequestStart(start);
    setRequestStop(stop);
  }, [start, stop, open]);

  const rangeMs =
    requestStart && requestStop
      ? new Date(requestStop).getTime() - new Date(requestStart).getTime()
      : 0;
  // Range > 2h
  const disableOneSec = rangeMs > 2 * 3600 * 1000;
  const isInvalidRange =
    !!requestStart &&
    !!requestStop &&
    new Date(requestStart).getTime() >= new Date(requestStop).getTime();

  const handleClose = () => {
    setInterval(-1);
    setData(null);
    setIncludeMinMax(false);
    setIncludeLastValue(false);
    setIncludeMissingData(false);
    onClose();
  };

  const CSVDownload = (props) => {
    const btnRef = useRef(null);
    useEffect(() => {
      btnRef.current?.click();
      handleClose();
    }, [btnRef]);
    return (
      <CSVLink {...props}>
        <span ref={btnRef} />
      </CSVLink>
    );
  };

  const fetchData = () => {
    if (!requestStart || !requestStop) return;
    if (isInvalidRange) return;
    setLoading(true);
    let loadInterval = interval === -1 ? defaultInterval : interval;

    const startStr = requestStart;
    const stopStr = requestStop;

    if (metricsUuids) {
      const queryResult = getTimeSeriesMetrics(
        siteUuid,
        startStr,
        stopStr,
        loadInterval,
        metricsUuids,
      );
      queryResult
        .then((res) => {
          setLoading(false);
          const Series = res.time_series;
          const csvData = getCsvData(
            Series,
            metricsUuids,
            site,
            includeMinMax,
            includeLastValue,
            includeMissingData,
          );
          setData(csvData);
        })
        .catch((err) => {
          console.error("Error in queryResult:", err);
          setLoading(false);
        });
    } else {
      getSiteTimeSeries(siteUuid, startStr, stopStr, loadInterval)
        .then((res) => {
          setLoading(false);
          const csvData = getCsvData(
            res.time_series,
            [],
            site,
            includeMinMax,
            includeLastValue,
            includeMissingData,
          );
          setData(csvData);
        })
        .catch((err) => {
          setLoading(false);
        });
    }
  };

  const handleDownloadClick = () => {
    fetchData();
  };

  /**
   * Converts the time series data from UUIDs to human readable fields
   */
  const getCsvData = (
    timeSeries,
    metrics,
    site,
    includeMinMax,
    includeLastValue,
    includeMissingData,
  ) => {
    const csvData = [];
    if (timeSeries.length === 0) return csvData;

    const header = ["bucket"];
    const firstBucket = timeSeries[0];
    const allKeys = Object.keys(firstBucket);
    const keysToInclude = [];

    allKeys.forEach((key) => {
      if (key === "bucket") return;

      let removed = "";
      const cleanedKey = key.replace(/_min|_max|_last_value/g, (match) => {
        removed = match;
        return "";
      });

      const hasMin = removed === "_min";
      const hasMax = removed === "_max";
      const hasLastValue = removed === "_last_value";
      const hasMissingData = key.startsWith("missing_data_");

      if ((hasMin || hasMax) && !includeMinMax) return;
      if (hasLastValue && !includeLastValue) return;
      if (hasMissingData && !includeMissingData) return;

      keysToInclude.push(key);

      if (metrics && metrics.length > 0) {
        const result = findSeries(cleanedKey, site);
        if (result && result.tsMetric) {
          header.push(
            result.assemblyName +
              " " +
              result.tsMetric.name +
              removed +
              " (" +
              result.tsMetric.unit +
              ")",
          );
        } else {
          header.push(key);
        }
      } else {
        header.push(key);
      }
    });
    csvData.push(header);

    timeSeries.forEach((bucket) => {
      const row = [];
      row.push(toISOStringWithTimezone(bucket.bucket, site.timezone));
      keysToInclude.forEach((key) => {
        row.push(bucket[key]);
      });
      csvData.push(row);
    });
    return csvData;
  };

  const handleStartChange = (newValue) => {
    if (!newValue) return;
    setRequestStart(newValue.tz(timezone).format("YYYY-MM-DDTHH:mm:ssZ"));
  };

  const handleStopChange = (newValue) => {
    if (!newValue) return;
    setRequestStop(newValue.tz(timezone).format("YYYY-MM-DDTHH:mm:ssZ"));
  };

  const getDurationText = () => {
    if (!requestStart || !requestStop) return "";
    const diff = Math.max(0, new Date(requestStop).getTime() - new Date(requestStart).getTime());
    const dayMs = 24 * 60 * 60 * 1000;
    const days = Math.floor(diff / dayMs);
    const minutes = Math.floor((diff % dayMs) / (60 * 1000));
    return `${days} days, ${minutes} minutes`;
  };

  return (
    <Dialog open={open}>
      <Box sx={{ px: "2.25rem", py: "2.5rem" }}>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Download Power Data
        </Typography>
        {/* Time Range Pickers */}
        <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" alignItems="center">
              <Box mr={1}>
                <DateTimePicker
                  label="Time Range Start"
                  value={requestStart ? dayjs(requestStart) : null}
                  onChange={handleStartChange}
                  timezone={timezone}
                  format="MM/DD/YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" sx={{ mx: 1 }}>
                -
              </Typography>
              <Box ml={1}>
                <DateTimePicker
                  label="Time Range End"
                  value={requestStop ? dayjs(requestStop) : null}
                  onChange={handleStopChange}
                  timezone={timezone}
                  format="MM/DD/YYYY hh:mm A"
                  slotProps={{
                    textField: {
                      size: "small",
                      InputLabelProps: { shrink: true },
                    },
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        </Box>
        <Typography variant="body2" sx={{ mb: 4 }}>
          {getDurationText()}
        </Typography>
        <FormControl fullWidth sx={{ mb: 3 }}>
          <InputLabel id="interval-select-id">Data Interval</InputLabel>
          <Select
            labelId="demo-simple-select-label"
            value={interval}
            label="Data Interval"
            onChange={(ev) => setInterval(ev.target.value)}
            notched={true}
          >
            <MenuItem value={-1}>As shown on graph</MenuItem>
            {disableOneSec ? (
              <Tooltip
                title="1-sec data interval is available for time ranges of 2 hrs or less."
                arrow
              >
                <span>
                  <MenuItem value={1} disabled style={{ pointerEvents: "none" }}>
                    1 sec
                  </MenuItem>
                </span>
              </Tooltip>
            ) : (
              <MenuItem value={1}>1 sec</MenuItem>
            )}
            <MenuItem value={60}>1 minute</MenuItem>
            <MenuItem value={900}>15 minutes</MenuItem>
            <MenuItem value={3600}>Hourly</MenuItem>
          </Select>
        </FormControl>
        <Typography color="text.primary" variant="subtitle2" sx={{ mb: 1 }}>
          Include
        </Typography>
        <Box display="flex" gap={2} sx={{ mb: 2 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={includeMinMax}
                onChange={(e) => setIncludeMinMax(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Min. & Max. Values</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={includeLastValue}
                onChange={(e) => setIncludeLastValue(e.target.checked)}
              />
            }
            label={<Typography variant="body2">Last Value</Typography>}
          />
          {!metricsUuids && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeMissingData}
                  onChange={(e) => setIncludeMissingData(e.target.checked)}
                />
              }
              label={<Typography variant="body2">Missing Data</Typography>}
            />
          )}
        </Box>
        <Box pt="2em" display="flex" flexDirection="row" width="100%" justifyContent="end">
          <Button
            onClick={handleClose}
            variant="outlined"
            color="inherit"
            sx={{ marginLeft: "1em", width: "150px" }}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={loading}
            onClick={handleDownloadClick}
            variant="contained"
            sx={{ marginLeft: "1em", width: "150px" }}
            disabled={isInvalidRange}
          >
            Download
          </LoadingButton>
        </Box>
        {data ? (
          <CSVDownload
            data={data}
            target="_blank"
            filename={`scaleos-metrics-export-${siteName}-${new Date().toISOString()}.csv`}
          />
        ) : null}
      </Box>
    </Dialog>
  );
};

DownloadDialog.propTypes = {
  start: PropTypes.string,
  stop: PropTypes.string,
  defaultInterval: PropTypes.number,
  open: PropTypes.bool,
  onClose: PropTypes.func,
  siteUuid: PropTypes.string,
  metricsUuids: PropTypes.arrayOf(PropTypes.string),
  site: PropTypes.object,
  siteName: PropTypes.string,
};
export default DownloadDialog;
